import type { DesktopBridge } from './native';

/**
 * AudioEngine — everything that touches Web Audio.
 *
 *  mic  ──► (AnalyserNode meter) ──► producer track (mediasoup)
 *       └─► sidetone gain ──► master ──► destination            (optional self-monitor)
 *
 *  each remote consumer track ──► <audio muted> (Chrome needs an element to pull RTP)
 *                              └─► MediaStreamSource ──► gain(key volume × duck) ──► master ──► destination
 *
 * IFB ducking is applied here: program feeds for channel X are attenuated by the channel's
 * dimDb whenever some other user is talking on ch:X.
 *
 * Multichannel output (audio interfaces, Dante Virtual Soundcard / Dante Via): with routing on, the
 * destination runs in discrete mode at the device's full channel count, and each source's gain
 * feeds a bus for its assigned output ("main" = channels 1–2, "m:N" = channel N, "s:N" = N and N+1)
 * through one ChannelMerger:
 *
 *   gain ─► bus(spec) ─► splitter ─► merger[ch…] ─► destination (N discrete channels)
 */

/** Where a source plays: 'main' (the stereo mix, channels 1–2), 'm:N' (mono on channel N) or 's:N' (stereo on N, N+1). */
export type OutputSpec = string;

export interface RemoteSource {
  id: string;
  role: 'mic' | 'program';
  channelId?: string;
  peerId: string;
  stream: MediaStream;
  el: HTMLAudioElement;
  source: MediaStreamAudioSourceNode;
  gain: GainNode;
  /** Volume 0..1 from the key slider (before ducking). */
  volume: number;
  /** Extra attenuation in dB currently applied (IFB dim). */
  duckDb: number;
  routed: boolean;
}

export const dbToGain = (db: number): number => (db <= -100 ? 0 : Math.pow(10, db / 20));

export class AudioEngine {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  private sidetone: GainNode | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private analyserBuf: Float32Array<ArrayBuffer> | null = null;
  readonly remotes = new Map<string, RemoteSource>();
  private sinkId: string | null = null;
  masterVolume = 1;
  /** Multichannel routing requested (desktop app with an interface selected as output). */
  private multichannel = false;
  private merger: ChannelMergerNode | null = null;
  private readonly buses = new Map<OutputSpec, GainNode>();
  private readonly outputs = new Map<string, OutputSpec>();

  /** Must be called from a user gesture on iOS/Safari. Safe to call repeatedly. */
  async ensure(): Promise<AudioContext> {
    if (!this.ctx) {
      const ctx = new AudioContext({ latencyHint: 'interactive', sampleRate: 48000 });
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.gain.value = this.masterVolume;
      this.master.connect(ctx.destination);
      this.sidetone = ctx.createGain();
      this.sidetone.gain.value = 0;
      this.sidetone.connect(this.master);
      // iOS 17+: ask for the play-and-record session so the speaker (not the earpiece) is used.
      const nav = navigator as Navigator & { audioSession?: { type: string } };
      if (nav.audioSession) {
        try {
          nav.audioSession.type = 'play-and-record';
        } catch {
          /* ignore */
        }
      }
      if (this.sinkId) await this.applySink();
    }
    if (this.ctx.state !== 'running') await this.ctx.resume().catch(() => undefined);
    return this.ctx;
  }

  setMasterVolume(v: number): void {
    this.masterVolume = v;
    if (!this.ctx) return;
    if (this.master) this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.02);
    for (const bus of this.buses.values()) bus.gain.setTargetAtTime(v, this.ctx.currentTime, 0.02);
  }

  async setSink(deviceId: string | null): Promise<void> {
    this.sinkId = deviceId;
    if (this.ctx) await this.applySink();
  }

  private async applySink(): Promise<void> {
    const ctx = this.ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
    if (!ctx.setSinkId) return;
    try {
      await ctx.setSinkId(this.sinkId ?? '');
    } catch (err) {
      console.warn('setSinkId failed', err);
    }
    // A new device may have a different channel count.
    this.rebuildOutputs();
  }

  // ---- multichannel output -----------------------------------------------------------------------

  /** Output channels available on the current device (2 for ordinary speakers/headphones). */
  outputChannelCount(): number {
    return this.ctx ? Math.max(2, this.ctx.destination.maxChannelCount) : 2;
  }

  setMultichannel(on: boolean): void {
    if (this.multichannel === on) return;
    this.multichannel = on;
    this.rebuildOutputs();
  }

  /** Route one source to an output ('main' unless multichannel routing is on). */
  setOutput(id: string, spec: OutputSpec): void {
    const r = this.remotes.get(id);
    if (!r || this.outputs.get(id) === spec) return;
    this.outputs.set(id, spec);
    r.gain.disconnect();
    r.gain.connect(this.busFor(spec));
  }

  private busFor(spec: OutputSpec): AudioNode {
    const ctx = this.ctx;
    const n = this.merger?.numberOfInputs ?? 0;
    const m = /^([ms]):(\d+)$/.exec(spec);
    if (!ctx || !this.master || !this.merger || !m) return this.master as AudioNode;
    const stereo = m[1] === 's';
    const first = Number(m[2]) - 1;
    if (first < 0 || first + (stereo ? 1 : 0) >= n) return this.master;
    const existing = this.buses.get(spec);
    if (existing) return existing;
    // A mono bus downmixes whatever arrives (stereo program feeds included) onto its one channel.
    const bus = new GainNode(ctx, { gain: this.masterVolume, channelCount: stereo ? 2 : 1, channelCountMode: 'explicit', channelInterpretation: 'speakers' });
    if (stereo) {
      const split = ctx.createChannelSplitter(2);
      bus.connect(split);
      split.connect(this.merger, 0, first);
      split.connect(this.merger, 1, first + 1);
    } else {
      bus.connect(this.merger, 0, first);
    }
    this.buses.set(spec, bus);
    return bus;
  }

  /** (Re)wire master and buses for the current device and routing mode, then re-attach every source. */
  private rebuildOutputs(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    this.master.disconnect();
    for (const bus of this.buses.values()) bus.disconnect();
    this.buses.clear();
    this.merger?.disconnect();
    this.merger = null;
    const dest = ctx.destination;
    const n = dest.maxChannelCount;
    if (this.multichannel && n > 2) {
      dest.channelCount = n;
      dest.channelCountMode = 'explicit';
      dest.channelInterpretation = 'discrete';
      this.merger = ctx.createChannelMerger(n);
      this.merger.connect(dest);
      const split = ctx.createChannelSplitter(2);
      this.master.connect(split);
      split.connect(this.merger, 0, 0);
      split.connect(this.merger, 1, 1);
    } else {
      dest.channelCount = 2;
      dest.channelCountMode = 'explicit';
      dest.channelInterpretation = 'speakers';
      this.master.connect(dest);
    }
    for (const [id, spec] of this.outputs) {
      const r = this.remotes.get(id);
      if (!r) continue;
      r.gain.disconnect();
      r.gain.connect(this.busFor(spec));
    }
  }

  // ---- microphone ------------------------------------------------------------------------------

  attachMic(stream: MediaStream): void {
    const ctx = this.ctx;
    if (!ctx || !this.sidetone) return;
    this.detachMic();
    this.micSource = ctx.createMediaStreamSource(stream);
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyserBuf = new Float32Array(this.analyser.fftSize) as Float32Array<ArrayBuffer>;
    this.micSource.connect(this.analyser);
    this.micSource.connect(this.sidetone);
  }

  detachMic(): void {
    this.micSource?.disconnect();
    this.micSource = null;
    this.analyser = null;
    this.analyserBuf = null;
  }

  setSidetone(level: number): void {
    if (this.sidetone && this.ctx) this.sidetone.gain.setTargetAtTime(level, this.ctx.currentTime, 0.02);
  }

  /** RMS mic level in dBFS (−100 when silent / no mic). */
  micLevelDb(): number {
    if (!this.analyser || !this.analyserBuf) return -100;
    this.analyser.getFloatTimeDomainData(this.analyserBuf);
    let sum = 0;
    for (let i = 0; i < this.analyserBuf.length; i++) sum += this.analyserBuf[i] * this.analyserBuf[i];
    const rms = Math.sqrt(sum / this.analyserBuf.length);
    return rms > 0 ? Math.max(-100, 20 * Math.log10(rms)) : -100;
  }

  // ---- remote sources --------------------------------------------------------------------------

  addRemote(id: string, track: MediaStreamTrack, meta: { role: 'mic' | 'program'; channelId?: string; peerId: string }, volume: number): RemoteSource {
    const ctx = this.ctx;
    if (!ctx || !this.master) throw new Error('audio context not started');
    this.removeRemote(id);
    const stream = new MediaStream([track]);
    // Chrome only decodes a remote track once something consumes it; a muted <audio> does that.
    const el = document.createElement('audio');
    el.srcObject = stream;
    el.muted = true;
    el.autoplay = true;
    el.setAttribute('playsinline', '');
    el.play().catch(() => undefined);
    const source = ctx.createMediaStreamSource(stream);
    const gain = ctx.createGain();
    gain.gain.value = 0;
    source.connect(gain);
    gain.connect(this.master);
    const remote: RemoteSource = { id, role: meta.role, channelId: meta.channelId, peerId: meta.peerId, stream, el, source, gain, volume, duckDb: 0, routed: false };
    this.remotes.set(id, remote);
    return remote;
  }

  removeRemote(id: string): void {
    const r = this.remotes.get(id);
    if (!r) return;
    r.source.disconnect();
    r.gain.disconnect();
    r.el.srcObject = null;
    r.el.remove();
    for (const t of r.stream.getTracks()) t.stop();
    this.remotes.delete(id);
    this.outputs.delete(id);
  }

  setRouted(id: string, routed: boolean): void {
    const r = this.remotes.get(id);
    if (!r) return;
    r.routed = routed;
    this.applyGain(r);
  }

  setVolume(id: string, volume: number): void {
    const r = this.remotes.get(id);
    if (!r) return;
    r.volume = volume;
    this.applyGain(r);
  }

  setDuck(id: string, duckDb: number): void {
    const r = this.remotes.get(id);
    if (!r || r.duckDb === duckDb) return;
    r.duckDb = duckDb;
    this.applyGain(r);
  }

  private applyGain(r: RemoteSource): void {
    if (!this.ctx) return;
    const target = r.routed ? r.volume * dbToGain(r.duckDb) : 0;
    // Short ramp: fast enough for PTT, slow enough to avoid clicks.
    r.gain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.015);
  }

  // ---- signalling tones --------------------------------------------------------------------------

  /** Two-tone call chime (used for incoming CALL signals). */
  chime(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    for (const [i, f] of [880, 1174.66].entries()) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0, t + i * 0.12);
      g.gain.linearRampToValueAtTime(0.25, t + i * 0.12 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 0.25);
      osc.connect(g).connect(this.master);
      osc.start(t + i * 0.12);
      osc.stop(t + i * 0.12 + 0.3);
    }
  }

  /** Short click for PTT press/release feedback. */
  click(on: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = on ? 1600 : 1000;
    g.gain.setValueAtTime(0.08, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.05);
  }
}

export async function listDevices(): Promise<{ inputs: MediaDeviceInfo[]; outputs: MediaDeviceInfo[] }> {
  const all = await navigator.mediaDevices.enumerateDevices();
  return { inputs: all.filter((d) => d.kind === 'audioinput'), outputs: all.filter((d) => d.kind === 'audiooutput') };
}

export interface MicOptions {
  deviceId: string | null;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
}

export async function openMic(opts: MicOptions): Promise<MediaStream> {
  const constraints: MediaStreamConstraints = {
    audio: {
      deviceId: opts.deviceId ? { exact: opts.deviceId } : undefined,
      echoCancellation: opts.echoCancellation,
      noiseSuppression: opts.noiseSuppression,
      autoGainControl: opts.autoGainControl,
      channelCount: 1,
    },
    video: false,
  };
  return navigator.mediaDevices.getUserMedia(constraints);
}

/** Program feed: raw line-level input, no processing, stereo if the device offers it. */
export async function openProgramInput(deviceId: string | null): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 2,
    },
    video: false,
  });
}

// ---- interface microphone (desktop app) ----------------------------------------------------------

/**
 * Jitter buffer between the desktop app's native capture (ffmpeg → IPC, ~10 ms chunks) and the
 * audio clock. Starts playing once 20 ms is queued, re-primes after an underrun, and drops the
 * oldest audio above 200 ms so a capture device running on a different clock (Dante) can't
 * build up delay.
 */
const PCM_WORKLET = `
class PcmIn extends AudioWorkletProcessor {
  constructor() {
    super();
    this.q = []; this.off = 0; this.len = 0; this.primed = false;
    this.port.onmessage = (e) => {
      this.q.push(e.data); this.len += e.data.length;
      while (this.len > 9600 && this.q.length > 1) { const x = this.q.shift(); this.len -= x.length - this.off; this.off = 0; }
    };
  }
  process(_inputs, outputs) {
    const out = outputs[0][0];
    if (!this.primed) { if (this.len < 960) { out.fill(0); return true; } this.primed = true; }
    let i = 0;
    while (i < out.length && this.q.length) {
      const b = this.q[0]; const n = Math.min(out.length - i, b.length - this.off);
      out.set(b.subarray(this.off, this.off + n), i);
      i += n; this.off += n; this.len -= n;
      if (this.off >= b.length) { this.q.shift(); this.off = 0; }
    }
    if (i < out.length) { out.fill(0, i); this.primed = false; }
    return true;
  }
}
registerProcessor('bccomms-pcm-in', PcmIn);
`;

let workletLoaded: Promise<void> | null = null;

export interface InterfaceMic {
  stream: MediaStream;
  stop(): void;
}

/** One channel of an audio interface (e.g. a Dante Virtual Soundcard channel) as a mono mic track. */
export async function openInterfaceMic(ctx: AudioContext, bridge: DesktopBridge, device: string, channel: number): Promise<InterfaceMic> {
  if (!workletLoaded) {
    const url = URL.createObjectURL(new Blob([PCM_WORKLET], { type: 'application/javascript' }));
    workletLoaded = ctx.audioWorklet.addModule(url);
  }
  await workletLoaded;
  const node = new AudioWorkletNode(ctx, 'bccomms-pcm-in', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1] });
  const dest = ctx.createMediaStreamDestination();
  dest.channelCount = 1;
  node.connect(dest);
  const offPcm = bridge.onPcm((samples) => node.port.postMessage(samples, [samples.buffer]));
  await bridge.startCapture(device, channel);
  return {
    stream: dest.stream,
    stop() {
      offPcm();
      void bridge.stopCapture();
      node.disconnect();
      for (const t of dest.stream.getTracks()) t.stop();
    },
  };
}
